import axios from "axios";
import classNames from "classnames";
import { SERVER_URL } from "Constants";
import { observer } from "mobx-react";
import { useState } from "react";
import alertToast from "Util/ToastifyUtils";
import { Button, Colors, Display, Sizes } from "Views/Components/Button/Button";
import ButtonAsyncState from "Views/Components/Button/ButtonAsyncState";
import { ButtonGroup } from "Views/Components/Button/ButtonGroup";
import EntityList from "Views/Components/EntityList/EntityList";
import FileUpload from "Views/Components/FileUpload/FileUpload";
import Modal from "Views/Components/Modal/Modal";
import TablePagination from "Views/Components/Pagination/TablePagination";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { useAppSettingStore } from "../SettingStores/AppSettingPageStore";
import { ProviderProductCreateDto } from "../types/CreditProviderProductStoreType";

const ValidateProductData = (product: ProviderProductCreateDto, errors: string[]) => {
	if (!!!product.name){
		errors.push("Product Name");
	}
	if (!!!product.productCode){
		errors.push("Product Code");
	}
	if (!!!product.baseCost){
		errors.push("Base Cost");
	}
}

const DEFAULT_PAGE_SIZE = 2;

interface Props {
	providerId: string;
	providerName: string;
	onClose: () => void;
}

export const ProviderProductUploadModal: React.FunctionComponent<Props> = observer(({
	providerId,
	providerName,
	onClose,
}) => {
	const { creditProviderProductStore: { isLoading, hasError, createProviderProducts } } = useAppSettingStore();

	const [uploadedFile, setUploadedFile] = useState<{file: File | undefined}>({file: undefined});
	const [providerProductCreateList, setProviderProductCreateList] = useState<ProviderProductCreateDto[]>([]);
	const [pageNo, setPageNo] = useState(0);
	const [isReadingFile, setIsReadingFile] = useState(false);
	const [errorList, setErrorList] = useState<Record<string,string[]>>({});
	
	const handleDownloadTemplateClick = async () => {
		const url = `${SERVER_URL}/api/provider/${providerId}/product/template`;
		try {
			await axios.get(url, { responseType: 'blob' })
				.then(response => {
					console.log("Response:",response);
					const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
					const link = document.createElement('a');
					link.href = downloadUrl;
					link.download = `${providerName}_product_template.csv`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(downloadUrl);
				})
		} catch (error) {
			console.error("Error downloading file:",error);
		}
	}

	const handleFileChange = (file: File) => {
		setIsReadingFile(true);
		const reader = new FileReader();
		reader.onload = e => {
			const text = e.target?.result as string;
			const rows = text.split('\n').map(row => row.trim()).filter(row => row);

			// has atleast one row of data and one header row
			if (rows.length < 2) {
				alertToast("Invalid file content. Please provide at least one product row.", "error");
				setUploadedFile({file: undefined});
				setProviderProductCreateList([]);
				setIsReadingFile(false);
				return;
			}

			// get headers
			const headers = rows[0].split(',').map(header => header.trim());

			// error list
			const errors: Record<string, string[]> = {};

			// map csv rows to objects
			const parsedData: ProviderProductCreateDto[] = rows.slice(1).map((row, index) => {
				const values = row.split(',').map(value => value.trim());

				const parsedValue: ProviderProductCreateDto = {
					name: values[headers.indexOf("Product_Name *")] || "",
					productCode: values[headers.indexOf("Product_Code *")] || "",
					baseCost: parseFloat(parseFloat(values[headers.indexOf("Base_Cost *")]).toFixed(2)) || 0,
					isActive: values[headers.indexOf("Is_Active")].toLowerCase() === "true",
					activeOnCreate: values[headers.indexOf("Active_On_Create")].toLowerCase() === "true",
					description: values[headers.indexOf("Product_Description")] || undefined,
					s3FileKey: values[headers.indexOf("S3_File_Key")] || undefined,
					countryCode: values[headers.indexOf("Country_Code")] || undefined,
				}

				// check for missing required fields
				errors[index + 2] = [];
				ValidateProductData(parsedValue, errors[index + 2]);

				return parsedValue;
			});

			setErrorList(errors);
			setProviderProductCreateList(parsedData);
			setIsReadingFile(false);
		}
		reader.readAsText(file);
	}

	const handleAddProducts = async () => {
		await createProviderProducts(providerProductCreateList, providerId);
		onClose();
	}

	const hasErrorOnUpload = Object.values(errorList).filter(list => list.length > 0).length > 0;

	return (
		<Modal
			className="add-provider-product"
			label="Upload Provider Product"
			isOpen
		>
			<div className="modal__header">
				<h3>Add new product(s) for {providerName}</h3>
				<Button
					className="modal-close"
					key="header-cancel"
					icon={{ icon: 'cross', iconPos: 'icon-only' }}
					display={Display.Text}
					onClick={onClose}
					sizes={Sizes.Medium}
					colors={Colors.Error}
					labelVisible={false}
					disabled={isLoading}
				>
					Cancel
				</Button>
			</div>
			<div className="modal__body">
				<div className="stack vertical start">
					<p>Download the product upload template</p>
					<Button
						display={Display.Solid}
						colors={Colors.Primary}
						sizes={Sizes.Medium}
						onClick={handleDownloadTemplateClick}
					>
						Download the template file
					</Button>
				</div>
				<div className="stack vertical start">
					<FileUpload
						tooltip="Upload a CSV file containing the product details."
						model={uploadedFile}
						modelProperty="file"
						contentType="text/csv,.csv"
						label={`Upload ${providerName} product as CSV*`}
						labelVisible
						preview
						onAfterChange={(file: File) => handleFileChange(file)}
						onDelete={() => {
							setProviderProductCreateList([]);
							setUploadedFile({file: undefined});
							setErrorList({});
						}}
					/>
					{isReadingFile && (
						<div className="stack vertical grow">
							<InlineSpinner/>
							<p>Reading the file...</p>
						</div>
					)}
					{hasErrorOnUpload && (
						<>
							{Object.entries(errorList).map((value, index) => {
								const [key, errorsAtRow] = value;
								return (
									<>
										{errorsAtRow.length > 0 && (
											<p className="alert alert__error" key={index}>
												At Row {key}: Missing Field(s):- {errorsAtRow.join(", ")}
											</p>
										)}
									</> 
								)
							})}
						</>
					)}
					{providerProductCreateList.length > 0 && (
						<div className="collection-component">
							<h5>Products to be added ({providerProductCreateList.length})</h5>
							<EntityList
								collection={providerProductCreateList.slice(pageNo * DEFAULT_PAGE_SIZE, (pageNo + 1) * DEFAULT_PAGE_SIZE)}
								columns={[
									{
										displayName: "Product Name",
										columnName: "productName",
										value: item => item.name,
									},
									{
										displayName: "Product Code",
										columnName: "productCode",
										value: item => item.productCode,
									},
									{
										displayName: "Base Cost",
										columnName: "baseCost",
										value: item => `$${item.baseCost.toFixed(2)}`,
									},
									{
										displayName: "Description",
										columnName: "description",
										value: item => item.description,
										showNA: true,
									},
									{
										displayName: "Country Code",
										columnName: "countryCode",
										value: item => item.countryCode,
										showNA: true,
									},
									{
										displayName: "Is Active",
										columnName: "isActive",
										value: item => (
											<span
												className={classNames("alert-box mini", { error: !item.isActive, success: item.isActive })}
												style={{background: "none"}}
											/>
										)
									},
									{
										displayName: "Active On Create",
										columnName: "activeOnCreate",
										value: item => (
											<span
												className={classNames("alert-box mini", { error: !item.activeOnCreate, success: item.activeOnCreate })}
												style={{background: "none"}}
											/>
										)
									},
									{
										displayName: "S3 File Key",
										columnName: "s3FileKey",
										value: item =>item.s3FileKey,
										showNA: true,
									}
								]}
								idColumn="productCode"
								sortColumn="productName"
								sortDescending
							/>
							<TablePagination
								perPage={DEFAULT_PAGE_SIZE}
								pageNo={pageNo}
								totalRecords={providerProductCreateList.length}
								onPageChange={newPageNo => setPageNo(newPageNo)}
							/>
						</div>
					)}
				</div>
			</div>
			<ButtonGroup className="modal__actions">
				<Button
					display={Display.Outline}
					sizes={Sizes.Medium}
					colors={Colors.Error}
					onClick={() => {
						setProviderProductCreateList([]);
						setUploadedFile({file: undefined});
						onClose();
					}}
					disabled={isLoading}
				>
					Cancel
				</Button>
				<ButtonAsyncState
					display={Display.Solid}
					sizes={Sizes.Medium}
					colors={Colors.Primary}
					onPress={handleAddProducts}
					readonly={providerProductCreateList.length === 0 || isLoading || hasErrorOnUpload }
				>
					Create Products
				</ButtonAsyncState>
			</ButtonGroup>
		</Modal>
	)
});